import * as React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { SeoItem, TitleMetaOpportunity } from '../types';
import ChangeActions from './ChangeActions';
import ChangeStatus from './ChangeStatus';

interface Props {
  item: SeoItem;
  onApprove: (item: SeoItem) => Promise<void>;
  onReject: (item: SeoItem, reason: string) => Promise<void>;
  onRequestChanges: (item: SeoItem, feedback: string) => Promise<void>;
  onUndo: (item: SeoItem) => Promise<void>;
}

function GoogleResult({
  title,
  description,
  url,
}: {
  title: string;
  description?: string;
  url: string;
}) {
  return (
    <Box>
      <Typography sx={{ color: '#1a0dab', fontSize: 18 }}>
        {title} – Dosaze
      </Typography>
      <Typography sx={{ color: '#006621', fontSize: 13 }}>
        {url.replace(/^https?:\/\//, '').replace(/\//g, ' › ')}
      </Typography>
      <Typography sx={{ fontSize: 14, color: '#4d5156' }}>
        {description || 'No description set; Google picks text from the page.'}
      </Typography>
    </Box>
  );
}

// A title/meta rewrite: how the article looks in Google before and after, and
// the buttons that apply to it (apply while waiting, undo while live).
export default function SnippetDetail({ item, ...actions }: Props) {
  const o = item.opportunity as TitleMetaOpportunity;
  const before = Object.fromEntries(
    (item.change?.fields ?? []).map(f => [f.field, f.before]),
  );
  return (
    <Stack spacing={2}>
      <ChangeStatus item={item} url={o.url} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Before
          </Typography>
          <GoogleResult
            title={before['Google title'] ?? o.articleTitle}
            description={
              before['Meta description'] === 'None set'
                ? undefined
                : before['Meta description']
            }
            url={o.url}
          />
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            After
          </Typography>
          <GoogleResult
            title={item.draft.title}
            description={item.draft.metaDescription}
            url={o.url}
          />
        </Paper>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Only the Google title and description change. The article’s heading,
        text and URL stay as they are. Google may take a few days to show the
        new version.
      </Typography>
      <ChangeActions
        item={item}
        applyText="The new title and description go live on the article right away. You can undo it here at any time."
        undoText="Puts back exactly the title and description that were live before, and stops its remaining checks."
        {...actions}
      />
    </Stack>
  );
}
